import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, ArrowLeft, Upload, Mic } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "ml", label: "Malayalam" },
  { code: "hi", label: "Hindi" },
];

const emptyEdition = () => ({ title: "", description: "", transcript: "" });
const emptyForm = { episode_number: "", title: "", description: "", related_article_slug: "" };

const PodcastForm = ({ episode, onSaved, onCancel }) => {
  const [form, setForm] = useState(episode ? {
    episode_number: episode.episode_number, title: episode.title,
    description: episode.description || "", related_article_slug: episode.related_article_slug || "",
  } : emptyForm);
  const [editions, setEditions] = useState(() => {
    const out = {};
    LANGUAGES.forEach(({ code }) => {
      const e = episode?.editions?.[code];
      out[code] = e ? { title: e.title || "", description: e.description || "", transcript: e.transcript || "" } : emptyEdition();
    });
    return out;
  });
  const [saved, setSaved] = useState(episode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setEdition = (code, k, v) => setEditions((prev) => ({ ...prev, [code]: { ...prev[code], [k]: v } }));

  const uploadCover = async (file) => {
    if (!saved?.id) return;
    setUploading((u) => ({ ...u, cover: true }));
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post(`/admin/podcasts/${saved.id}/cover`, body, { headers: { "Content-Type": "multipart/form-data" } });
      setSaved(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Cover upload failed");
    } finally {
      setUploading((u) => ({ ...u, cover: false }));
    }
  };

  const uploadAudio = async (code, file) => {
    if (!saved?.id) return;
    setUploading((u) => ({ ...u, [code]: true }));
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post(`/admin/podcasts/${saved.id}/editions/${code}/audio`, body, { headers: { "Content-Type": "multipart/form-data" } });
      setSaved(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Audio upload failed");
    } finally {
      setUploading((u) => ({ ...u, [code]: false }));
    }
  };

  const togglePublish = async (code, published) => {
    setError("");
    try {
      const { data } = await api.post(`/admin/podcasts/${saved.id}/editions/${code}/publish`, { published });
      setSaved(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not update publish status");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const wasNew = !saved?.id;
    try {
      const payload = {
        episode_number: Number(form.episode_number), title: form.title,
        description: form.description || null, related_article_slug: form.related_article_slug || null,
      };
      let current = saved;
      if (current?.id) {
        const { data } = await api.put(`/admin/podcasts/${current.id}`, payload);
        current = data;
      } else {
        const { data } = await api.post("/admin/podcasts", payload);
        current = data;
      }
      await Promise.all(LANGUAGES.map(({ code }) =>
        api.put(`/admin/podcasts/${current.id}/editions/${code}`, editions[code])
      ));
      setSaved(current);
      if (!wasNew) {
        onSaved();
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not save episode");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="rounded-xl border border-cts-line bg-white p-6 space-y-6" data-testid="podcast-form">
      <button type="button" onClick={onCancel} data-testid="podcast-form-back-btn" className="flex items-center gap-1.5 text-sm font-semibold text-cts-charcoal hover:text-cts-red">
        <ArrowLeft size={15} /> Back to Podcasts
      </button>

      {error && <p className="rounded-md bg-cts-redlight px-4 py-3 text-sm text-cts-ink" role="alert" data-testid="podcast-form-error">{error}</p>}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="field-label">Episode Number *</label>
          <input required type="number" min={1} data-testid="podcast-episode-number-input" className="field-input" value={form.episode_number} onChange={(e) => set("episode_number", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Related Technology Advice Article Slug (optional)</label>
          <input data-testid="podcast-related-article-input" className="field-input" value={form.related_article_slug} onChange={(e) => set("related_article_slug", e.target.value)} placeholder="e.g. how-much-ram-does-an-office-computer-need" />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Title *</label>
          <input required data-testid="podcast-title-input" className="field-input" value={form.title} onChange={(e) => set("title", e.target.value)} maxLength={150} />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Description</label>
          <textarea data-testid="podcast-description-input" className="field-input" rows={2} maxLength={1000} value={form.description} onChange={(e) => set("description", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Cover Image (shared by all languages)</label>
          <div className="flex gap-3 items-center">
            {saved?.has_cover && <span className="text-xs text-green-700 font-semibold">Uploaded</span>}
            {saved?.id ? (
              <label className="btn-outline !min-h-[44px] !py-2 cursor-pointer whitespace-nowrap">
                {uploading.cover ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />} UPLOAD COVER
                <input type="file" accept="image/*" className="hidden" data-testid="podcast-cover-file-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadCover(f); }} />
              </label>
            ) : (
              <p className="text-xs text-cts-charcoal/60">Save the episode once to enable cover and audio uploads.</p>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-5">
        <label className="field-label !mb-0">Language Editions</label>
        {LANGUAGES.map(({ code, label }) => {
          const adminEdition = saved?.editions?.[code];
          const isPublished = adminEdition?.status === "PUBLISHED";
          return (
            <div key={code} className="border border-cts-line rounded-lg p-4 space-y-3" data-testid={`podcast-edition-${code}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="font-heading font-bold text-cts-ink flex items-center gap-2"><Mic size={15} className="text-cts-red" /> {label}</p>
                {saved?.id && (
                  <div className="flex items-center gap-3">
                    <span className={`rounded px-2 py-1 text-xs font-bold ${isPublished ? "bg-green-100 text-green-800" : "bg-cts-soft text-cts-charcoal"}`}>
                      {isPublished ? "PUBLISHED" : "DRAFT"}
                    </span>
                    <button
                      type="button"
                      disabled={!adminEdition?.has_audio}
                      onClick={() => togglePublish(code, !isPublished)}
                      data-testid={`podcast-publish-toggle-${code}`}
                      className="text-xs font-bold text-cts-red disabled:opacity-40 disabled:cursor-not-allowed hover:underline"
                      title={!adminEdition?.has_audio ? "Upload audio before publishing" : undefined}
                    >
                      {isPublished ? "UNPUBLISH" : "PUBLISH"}
                    </button>
                  </div>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  placeholder={`${label} title (optional, defaults to main title)`}
                  className="field-input"
                  value={editions[code].title}
                  onChange={(e) => setEdition(code, "title", e.target.value)}
                  data-testid={`podcast-edition-title-${code}`}
                />
                <div className="flex items-center gap-3">
                  {adminEdition?.has_audio && <span className="text-xs text-green-700 font-semibold whitespace-nowrap">Audio uploaded</span>}
                  {saved?.id && (
                    <label className="btn-outline !min-h-[40px] !py-1.5 cursor-pointer whitespace-nowrap text-xs">
                      {uploading[code] ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} {adminEdition?.has_audio ? "REPLACE AUDIO" : "UPLOAD AUDIO"}
                      <input type="file" accept=".mp3,.m4a,audio/mpeg,audio/mp4" className="hidden" data-testid={`podcast-audio-file-input-${code}`} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadAudio(code, f); }} />
                    </label>
                  )}
                </div>
                <textarea
                  placeholder={`${label} description (optional)`}
                  className="field-input sm:col-span-2"
                  rows={2}
                  value={editions[code].description}
                  onChange={(e) => setEdition(code, "description", e.target.value)}
                  data-testid={`podcast-edition-description-${code}`}
                />
                <textarea
                  placeholder={`${label} transcript (optional)`}
                  className="field-input sm:col-span-2"
                  rows={4}
                  value={editions[code].transcript}
                  onChange={(e) => setEdition(code, "transcript", e.target.value)}
                  data-testid={`podcast-edition-transcript-${code}`}
                />
              </div>
            </div>
          );
        })}
      </div>

      <button type="submit" disabled={saving} data-testid="podcast-save-btn" className="btn-primary disabled:opacity-60">
        {saving ? <Loader2 className="animate-spin" size={16} /> : "SAVE EPISODE"}
      </button>
    </form>
  );
};

export const PodcastsTab = () => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/podcasts");
      setEpisodes(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id, title) => {
    if (!window.confirm(`Delete episode "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/podcasts/${id}`);
      load();
    } catch (err) {
      window.alert(formatApiError(err.response?.data?.detail) || "Could not delete episode");
    }
  };

  if (editing !== null) {
    return (
      <PodcastForm
        episode={editing.id ? editing : null}
        onSaved={() => { setEditing(null); load(); }}
        onCancel={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div data-testid="admin-podcasts-tab">
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({})} data-testid="podcast-new-btn" className="btn-primary">
          <Plus size={16} /> NEW EPISODE
        </button>
      </div>
      {loading ? (
        <div className="text-center py-10"><Loader2 className="animate-spin mx-auto text-cts-red" size={24} /></div>
      ) : episodes.length === 0 ? (
        <div className="rounded-xl border border-cts-line bg-white p-14 text-center" data-testid="admin-podcasts-empty">
          <p className="font-heading font-bold text-cts-ink">No episodes yet</p>
          <p className="text-sm text-cts-charcoal mt-1">Create the first CTS Business Insights episode.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-cts-line bg-white" data-testid="admin-podcasts-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cts-line text-left">
                {["Episode", "Title", "English", "Malayalam", "Hindi", "Updated", ""].map((h) => (
                  <th key={h} className="px-4 py-3 font-heading font-bold text-xs uppercase tracking-wide text-cts-charcoal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {episodes.map((ep) => (
                <tr key={ep.id} className="border-b border-cts-line last:border-0" data-testid={`podcast-row-${ep.id}`}>
                  <td className="px-4 py-3 font-semibold text-cts-ink">#{ep.episode_number}</td>
                  <td className="px-4 py-3 text-cts-charcoal">{ep.title}</td>
                  {["en", "ml", "hi"].map((code) => (
                    <td key={code} className="px-4 py-3">
                      <span className={`rounded px-2 py-1 text-xs font-bold ${ep.editions[code].status === "PUBLISHED" ? "bg-green-100 text-green-800" : "bg-cts-soft text-cts-charcoal"}`}>
                        {ep.editions[code].status}
                      </span>
                    </td>
                  ))}
                  <td className="px-4 py-3 whitespace-nowrap text-cts-charcoal">{new Date(ep.updated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                  <td className="px-4 py-3 flex items-center gap-3">
                    <button onClick={() => setEditing(ep)} data-testid={`podcast-edit-${ep.id}`} className="text-cts-charcoal hover:text-cts-red" aria-label={`Edit episode ${ep.episode_number}`}><Pencil size={16} /></button>
                    <button onClick={() => onDelete(ep.id, ep.title)} data-testid={`podcast-delete-${ep.id}`} className="text-cts-charcoal/60 hover:text-cts-red" aria-label={`Delete episode ${ep.episode_number}`}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
